import React from 'react';
import { SchematicShell, Line } from './SchematicPrimitives';
import {
  microwaveFaceLayout,
  trimKitLayout,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

const IN = 0.0254;

/**
 * A countertop microwave on an elevation: the face the 3D one wears — the
 * door's window, the control panel's divider, its display, keypad, stop /
 * start and door-release buttons, and the handle — every piece from the one
 * shared layout (`proceduralSpecs.microwaveFaceLayout`), so the two cannot
 * disagree. A built-in one (`microwaveCabinets.microwaveFor`, `trimKit`)
 * carries its trim frame round it, out to the opening it fills, with the
 * vent slots the 3D one has (`trimKitLayout`).
 */
export function DormMicrowaveSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  obj = {},
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    );
  }

  const face = microwaveFaceLayout(width, height);
  // a layout rect ({ x centre about the face's middle, y centre up from its
  // bottom }) as SVG, whose y runs down from the top
  const box = (part, r, extra = {}) => (
    <rect
      key={`${part}-${r.x}-${r.y}`}
      data-part={part}
      x={width / 2 + r.x - r.w / 2}
      y={height - r.y - r.h / 2}
      width={r.w}
      height={r.h}
      fill="none"
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
      {...extra}
    />
  );
  const panelLeft = width / 2 + face.panel.x - face.panel.w / 2;
  const kit =
    obj.trimKit?.width > 0 && obj.trimKit?.height > 0 ? obj.trimKit : null;
  const vents = kit ? trimKitLayout(width, height, kit).vents : [];

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {/* Door / panel divider */}
      <Line
        x1={panelLeft}
        y1={0}
        x2={panelLeft}
        y2={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {box('window', face.window, { rx: 0.25 * IN })}
      {box('handle', face.handle)}
      {box('display', face.display)}
      {face.keypad.map((k) => box('keypad', k))}
      {box('stop', face.stop)}
      {box('start', face.start)}
      {box('release', face.release)}
      {kit && (
        // the trim frame's outer edge; svg y runs down from the box's top
        <rect
          data-part="trim-kit"
          x={width / 2 - kit.width / 2}
          y={height / 2 - (kit.y || 0) - kit.height / 2}
          width={kit.width}
          height={kit.height}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          vectorEffect="non-scaling-stroke"
        />
      )}
      {vents.map((v) => box('trim-vent', v))}
    </SchematicShell>
  );
}

DormMicrowaveSchematic.supportsSideViews = true;
